import React, { useState } from 'react';
import MUIDataTable from "mui-datatables";
import { CircularProgress } from '@mui/material';
import FilterListOffOutlinedIcon from '@mui/icons-material/FilterListOffOutlined';
import './StyledTable.css';

/**
 * StyledTable — Reusable, attractive table with per-column filters
 * 
 * Props:
 * @param {string} title - Table title text
 * @param {React.ReactNode} icon - Optional icon element
 * @param {Array} data - Table data rows
 * @param {Array} columns - MUIDataTable column definitions
 * @param {Object} options - MUIDataTable options (merged with defaults)
 * @param {boolean} loading - Show loading spinner
 * @param {React.ReactNode} headerAction - Optional right-side header action
 * @param {string} emptyMessage - Message when no data
 * @param {string} subtitle - Optional subtitle text
 * @param {boolean} filterable - Enable per-column filter inputs (default: true)
 * @param {boolean} filterDisabled - Show filter UI but disabled (greyed out)
 */
const StyledTable = ({
  title,
  icon,
  data = [],
  columns = [],
  options = {},
  loading = false,
  headerAction,
  emptyMessage = "No records found",
  subtitle,
  filterable = true,
  filterDisabled = false,
}) => {
  const [filterList, setFilterList] = useState({});

  const handleFilterChange = (colIndex, value) => {
    if (filterDisabled) return;
    setFilterList(prev => ({
      ...prev,
      [colIndex]: value,
    }));
  };

  const clearAllFilters = () => {
    setFilterList({});
  };

  const hasActiveFilters = Object.values(filterList).some(v => v && v.length > 0);

  // Apply client-side filtering to data
  const filteredData = React.useMemo(() => {
    if (!hasActiveFilters || !data) return data;
    return data.filter(row => {
      return Object.entries(filterList).every(([colIdx, filterValue]) => {
        if (!filterValue || filterValue.length === 0) return true;
        const cellValue = String(row[parseInt(colIdx)] || '').toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
      });
    });
  }, [data, filterList, hasActiveFilters]);

  const defaultOptions = {
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    sort: true,
    selectableRows: "none",
    responsive: "standard",
    rowsPerPageOptions: [10, 25, 50],
    rowsPerPage: 10,
    elevation: 0,
    textLabels: {
      body: {
        noMatch: emptyMessage,
      },
    },
  };

  const mergedOptions = { ...defaultOptions, ...options };

  // If server-side, don't apply client filtering
  const tableData = mergedOptions.serverSide ? data : filteredData;

  return (
    <div className="ck-table-wrapper">
      <div className="ck-table-card">
        {/* Header */}
        <div className="ck-table-header">
          <div className="ck-table-header-left">
            <div className="ck-table-title-row">
              {icon && <span className="ck-table-icon">{icon}</span>}
              <h3 className="ck-table-title">{title}</h3>
            </div>
            {subtitle && <p className="ck-table-subtitle">{subtitle}</p>}
          </div>
          <div className="ck-table-header-right">
            {hasActiveFilters && !filterDisabled && (
              <button className="ck-table-clear-filter" onClick={clearAllFilters}>
                <FilterListOffOutlinedIcon style={{ fontSize: 16 }} />
                Clear Filters
              </button>
            )}
            {headerAction}
          </div>
        </div>

        {/* Column Filters */}
        {filterable && columns.length > 0 && (
          <div className={`ck-table-filters ${filterDisabled ? 'ck-table-filters--disabled' : ''}`}>
            <div className="ck-table-filters-row">
              {columns.map((col, idx) => {
                const colName = typeof col === 'string' ? col : col?.name;
                const isFilterable = typeof col === 'object' ? col?.options?.filter !== false : true;
                if (!isFilterable) return <div key={idx} className="ck-table-filter-cell ck-table-filter-cell--empty" />;
                return (
                  <div key={idx} className="ck-table-filter-cell">
                    <input
                      type="text"
                      className="ck-table-filter-input"
                      placeholder={`Filter ${colName || ''}...`}
                      value={filterList[idx] || ''}
                      onChange={(e) => handleFilterChange(idx, e.target.value)}
                      disabled={filterDisabled}
                      aria-label={`Filter by ${colName}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Table Body */}
        <div className="ck-table-body">
          {loading ? (
            <div className="ck-table-loading">
              <div className="ck-table-loading-inner">
                <CircularProgress style={{ color: 'var(--ck-green)' }} size={36} />
                <span className="ck-table-loading-text">Loading...</span>
              </div>
            </div>
          ) : (
            <MUIDataTable
              className="ck-mui-table"
              data={tableData}
              columns={columns}
              options={mergedOptions}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StyledTable;
